import { addDays } from '@/components/coverage/CoverageSection'
import { ariaCounts, shortDate, SIDE } from './format'

// "Who covered it, day by day" (the identity mocks, 2026-11-01; flipped to
// the concept the same day). Right-leaning articles rise above a 2px ink
// line, left-leaning ones hang below it, and center straddles it, half each
// side, the same reading as every row's timeline (ui/LeanTimeline). One
// scale for both halves.
//
// The whole window, not just the days with coverage (Sam, 2026-11-01): give
// `from` and `to` and every day between them gets a column, a quiet day a
// bare stretch of line, so a gap in the coverage reads as a gap. Without
// them the chart draws the days it's given.
//
// Two forms, by how many columns there are:
//   up to DETAILED days   a column per day with its counts, the date and
//                         "N center" under it; on a phone it turns sideways,
//                         one row per day, newest first.
//   more                  slim columns at every width, the counts in each
//                         column's tooltip, dates along the foot. Past
//                         WEEKLY_AFTER days the columns are weeks.

const DETAILED = 14
const WEEKLY_AFTER = 120
const UP = 128
const DENSE_UP = 84
const ACROSS = 110
const TICKS = 6

const EMPTY = { left: 0, center: 0, right: 0 }

function fill(days, from, to) {
  if (!from || !to || from > to) return days
  const byDay = new Map(days.map((d) => [d.day, d]))
  const out = []
  for (let day = from; day <= to; day = addDays(day, 1))
    out.push(byDay.get(day) || { day, wings: EMPTY })
  return out
}

// Weeks ending on the newest day, so the latest week is whole.
function weeks(days) {
  const out = []
  for (let end = days.length; end > 0; end -= 7) {
    const chunk = days.slice(Math.max(0, end - 7), end)
    const wings = { left: 0, center: 0, right: 0 }
    for (const d of chunk)
      for (const w of Object.keys(wings)) wings[w] += d.wings?.[w] || 0
    out.unshift({ day: chunk[0].day, to: chunk[chunk.length - 1].day, wings })
  }
  return out
}

const Caption = ({ children, className = '' }) => (
  <p className={`font-sans text-[13px] text-muted-foreground ${className}`}>
    {children}
  </p>
)

export default function StorylineDayChart({ days = [], from, to }) {
  const filled = fill(days, from, to)
  if (!filled.length) return null
  const weekly = filled.length > WEEKLY_AFTER
  const cols = weekly ? weeks(filled) : filled
  const dateOf = (d) =>
    d.to ? `${shortDate(d.day)} to ${shortDate(d.to)}` : shortDate(d.day)
  const aria = `${weekly ? 'Weekly' : 'Daily'} coverage. ${cols
    .map((d) => `${dateOf(d)}: ${ariaCounts(d.wings)}`)
    .join('; ')}`

  if (cols.length > DETAILED)
    return <Dense cols={cols} weekly={weekly} aria={aria} dateOf={dateOf} />
  return <Detailed days={cols} aria={aria} />
}

function Dense({ cols, weekly, aria, dateOf }) {
  const half = (d, w) => (d.wings[w] || 0) + (d.wings.center || 0) / 2
  const max = Math.max(
    1,
    ...cols.flatMap((d) => [half(d, 'left'), half(d, 'right')])
  )
  const px = (n) => `${((n / max) * DENSE_UP).toFixed(1)}px`
  const gap = cols.length > 45 ? 'gap-px' : 'gap-[3px]'
  // About six dates along the foot, always the first and the last.
  const step = Math.max(1, Math.ceil(cols.length / TICKS))
  const ticks = cols
    .map((d, i) => [d, i])
    .filter(([, i]) => i === 0 || i === cols.length - 1 || i % step === 0)
    .filter(([, i]) => i === cols.length - 1 || cols.length - 1 - i >= step / 2)

  return (
    <>
      <Caption className="mb-5">
        One column per {weekly ? 'week' : 'day'}. Right-leaning coverage goes
        up, left-leaning goes down, and center sits on the line. A flat stretch
        means no coverage.
      </Caption>
      <div role="img" aria-label={aria}>
        <div
          className={`grid ${gap}`}
          style={{
            gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))`,
          }}
        >
          {cols.map((d) => (
            <div
              key={d.day}
              className="flex min-w-0 flex-col"
              title={`${dateOf(d)}: ${ariaCounts(d.wings)}`}
            >
              <div
                className="flex flex-col justify-end"
                style={{ height: `${DENSE_UP}px` }}
              >
                <div
                  className={SIDE.right.bg}
                  style={{ height: px(d.wings.right || 0) }}
                />
                <div
                  className={SIDE.center.bg}
                  style={{ height: px((d.wings.center || 0) / 2) }}
                />
              </div>
              <div className="h-0.5 bg-foreground" />
              <div
                className="flex flex-col justify-start"
                style={{ height: `${DENSE_UP}px` }}
              >
                <div
                  className={SIDE.center.bg}
                  style={{ height: px((d.wings.center || 0) / 2) }}
                />
                <div
                  className={SIDE.left.bg}
                  style={{ height: px(d.wings.left || 0) }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="relative mt-2 h-4 font-sans text-[11px] font-semibold text-foreground">
          {ticks.map(([d, i]) => {
            const last = i === cols.length - 1
            const first = i === 0
            return (
              <span
                key={d.day}
                className="absolute whitespace-nowrap"
                style={
                  last
                    ? { right: 0 }
                    : {
                        left: `${((i + (first ? 0 : 0.5)) / cols.length) * 100}%`,
                        transform: first ? 'none' : 'translateX(-50%)',
                      }
                }
              >
                {shortDate(last && d.to ? d.to : d.day)}
              </span>
            )
          })}
        </div>
      </div>
    </>
  )
}

function Detailed({ days, aria }) {
  const half = (d, w) => (d.wings[w] || 0) + (d.wings.center || 0) / 2
  const max = Math.max(
    1,
    ...days.flatMap((d) => [half(d, 'left'), half(d, 'right')])
  )
  const across = Math.max(
    1,
    ...days.flatMap((d) => [d.wings.left || 0, d.wings.right || 0])
  )
  const px = (n) => `${Math.round((n / max) * UP)}px`
  const quiet = (d) =>
    !(d.wings.left || 0) && !(d.wings.center || 0) && !(d.wings.right || 0)
  const count = (w) =>
    `font-sans text-xs font-semibold tabular-nums ${SIDE[w].text}`

  return (
    <>
      <Caption className="mb-5 hidden sm:block">
        One column per day. Right-leaning coverage goes up, left-leaning goes
        down, and center sits on the line.
      </Caption>
      <Caption className="mb-3.5 text-xs sm:hidden">
        Left-leaning coverage on the left of each date, right-leaning on the
        right.
      </Caption>

      {/* Desktop: columns, oldest first. */}
      <div
        role="img"
        aria-label={aria}
        className="hidden overflow-x-auto sm:block"
      >
        <div
          className="grid min-w-[640px] gap-2.5"
          style={{
            gridTemplateColumns: `repeat(${days.length}, minmax(56px, 1fr))`,
          }}
        >
          {days.map((d) => (
            <div key={d.day} className="flex flex-col items-center">
              <span className={`h-[18px] ${count('right')}`}>
                {quiet(d) ? '' : d.wings.right || 0}
              </span>
              <div className="flex h-[132px] w-full flex-col items-center justify-end">
                <div
                  className={`w-[70%] max-w-[44px] ${SIDE.right.bg}`}
                  style={{ height: px(d.wings.right || 0) }}
                />
                <div
                  className={`w-[70%] max-w-[44px] ${SIDE.center.bg}`}
                  style={{ height: px((d.wings.center || 0) / 2) }}
                />
              </div>
              <div className="h-0.5 w-full bg-foreground" />
              <div className="flex h-[132px] w-full flex-col items-center justify-start">
                <div
                  className={`w-[70%] max-w-[44px] ${SIDE.center.bg}`}
                  style={{ height: px((d.wings.center || 0) / 2) }}
                />
                <div
                  className={`w-[70%] max-w-[44px] ${SIDE.left.bg}`}
                  style={{ height: px(d.wings.left || 0) }}
                />
              </div>
              <span className={`h-[18px] ${count('left')}`}>
                {quiet(d) ? '' : d.wings.left || 0}
              </span>
              <span className="mt-1.5 font-sans text-xs font-semibold text-foreground">
                {shortDate(d.day)}
              </span>
              <span className="font-sans text-[11px] text-muted-foreground">
                {quiet(d) ? 'no coverage' : `${d.wings.center || 0} center`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Phone: rows, newest first. */}
      <div className="sm:hidden">
        <div className="mb-1.5 grid grid-cols-[minmax(0,1fr)_54px_minmax(0,1fr)] font-sans text-[11px] font-semibold uppercase tracking-[0.12em]">
          <span className={`text-right ${SIDE.left.text}`}>Left</span>
          <span />
          <span className={SIDE.right.text}>Right</span>
        </div>
        <div role="img" aria-label={aria}>
          {[...days].reverse().map((d) => (
            <div
              key={d.day}
              className="grid h-[30px] grid-cols-[minmax(0,1fr)_54px_minmax(0,1fr)] items-center"
            >
              <div className="flex items-center justify-end gap-1.5">
                {!quiet(d) && (
                  <>
                    <span className={`text-[11px] ${count('left')}`}>
                      {d.wings.left || 0}
                    </span>
                    <span
                      className={`block h-3.5 ${SIDE.left.bg}`}
                      style={{
                        width: `${Math.max(1, Math.round(((d.wings.left || 0) / across) * ACROSS))}px`,
                      }}
                    />
                  </>
                )}
              </div>
              <span className="border-x-2 border-foreground text-center font-sans text-[11px] font-semibold leading-[30px] text-foreground">
                {shortDate(d.day)}
              </span>
              <div className="flex items-center gap-1.5">
                {quiet(d) ? (
                  <span className="font-sans text-[11px] text-muted-foreground">
                    no coverage
                  </span>
                ) : (
                  <>
                    <span
                      className={`block h-3.5 ${SIDE.right.bg}`}
                      style={{
                        width: `${Math.max(1, Math.round(((d.wings.right || 0) / across) * ACROSS))}px`,
                      }}
                    />
                    <span className={`text-[11px] ${count('right')}`}>
                      {d.wings.right || 0}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
